import {useCallback, useId, useRef, useState} from 'react';
import type React from 'react';

/**
 * WAI-ARIA tabs with a roving tabindex: only the selected tab is in the Tab order, the arrow
 * keys move between tabs (wrapping at the ends) and Home/End jump to the first/last.
 * Spread `getTabProps(i)` on each tab button and `getPanelProps(i)` on each panel.
 */
export const useRovingTabs = (count: number) => {
    const [active, setActive] = useState(0);
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const idPrefix = useId();

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLButtonElement>) => {
            const last = count - 1;
            const next = active === last ? 0 : active + 1;
            const previous = active === 0 ? last : active - 1;
            const keyTargets: Record<string, number> = {
                ArrowRight: next,
                ArrowDown: next,
                ArrowLeft: previous,
                ArrowUp: previous,
                Home: 0,
                End: last
            };
            const target = keyTargets[event.key];
            if (target === undefined) return;
            event.preventDefault();
            setActive(target);
            tabRefs.current[target]?.focus();
        },
        [active, count]
    );

    const getTabProps = (index: number) => ({
        role: 'tab' as const,
        type: 'button' as const,
        id: `${idPrefix}-tab-${index}`,
        'aria-selected': index === active,
        'aria-controls': `${idPrefix}-panel-${index}`,
        tabIndex: index === active ? 0 : -1,
        ref: (el: HTMLButtonElement | null) => {
            tabRefs.current[index] = el;
        },
        onClick: () => setActive(index),
        onKeyDown: handleKeyDown
    });

    const getPanelProps = (index: number) => ({
        role: 'tabpanel' as const,
        id: `${idPrefix}-panel-${index}`,
        'aria-labelledby': `${idPrefix}-tab-${index}`,
        hidden: index !== active
    });

    // `setActive` lets a caller select a tab from outside the click/keyboard flow (e.g. hover preview).
    return {active, setActive, getTabProps, getPanelProps};
};
